import InfoLayout from "../components/InfoLayout";
import usePageMeta from "../hooks/usePageMeta";
import { GuideBlock } from "../components/GuideBlocks";
import GuideRegSearch from "../components/GuideRegSearch";
import { slugifyHeading } from "../../lib/guides.js";
import { serviceBossGuideSchema } from "../../lib/serviceBossSeo.js";
import { pillarLinkFor, relatedGuidesFor } from "../content/relatedGuides.js";

// Generic renderer for any guide article. Content lives as plain data in
// src/content/guides.js, not JSX, so every article automatically gets the
// same structure: jump-to nav, headings, callouts, freshness date, and
// Article + BreadcrumbList JSON-LD for search and AI-answer-engine crawlers.
// ServiceByReg also gets an inline free-vehicle reg search (SBR only).
export default function GuideArticlePage({ brand, data }) {
  if (!data) return null;

  const path = data.slug.endsWith("/") ? data.slug.slice(0, -1) : data.slug;
  const canonical = `https://${brand.domain}${path}`;
  const isServiceBoss = brand.key === "serviceboss";
  usePageMeta({ title: `${data.metaTitle || data.title} | ${brand.name}`, description: data.metaDescription, canonical, ogType: isServiceBoss ? "article" : "website" });

  const sections = data.sections.map((section) => ({ ...section, id: slugifyHeading(section.heading) }));
  const showSbrRegSearch = brand.key === "servicebyreg";
  const related = relatedGuidesFor(brand, data.slug);
  const pillar = pillarLinkFor(brand);

  const articleSchema = isServiceBoss ? serviceBossGuideSchema(data, canonical) : {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.title,
    description: data.metaDescription,
    datePublished: data.published,
    dateModified: data.updated,
    author: { "@type": "Organization", name: brand.name, url: `https://${brand.domain}` },
    publisher: { "@type": "Organization", name: brand.name, url: `https://${brand.domain}` },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `https://${brand.domain}/` },
      { "@type": "ListItem", position: 2, name: "Guides", item: `https://${brand.domain}/guides/` },
      { "@type": "ListItem", position: 3, name: data.title, item: canonical },
    ],
  };

  // FAQPage schema only when an article opts in with faqSchema: true.
  const faqSchema = data.faqSchema && data.faq && !isServiceBoss
    ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: data.faq.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })),
    }
    : null;

  // The prerendered page already carries this article's JSON-LD in <head>;
  // rendering it again would give Google duplicate Article and FAQPage blocks.
  const schemaInHead = typeof document !== "undefined" && [...document.head.querySelectorAll('script[type="application/ld+json"]')]
    .some((script) => script.textContent.includes(`"headline":${JSON.stringify(data.title)}`));

  return (
    <>
      {!schemaInHead && (
        <>
          <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
          {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
        </>
      )}
      <InfoLayout brand={brand} eyebrow="Guide" title={data.title} intro={data.intro}>
        <section className="content">
          <div className="wrap prose">
            <div className="guide-meta-row">
              {data.category && <span className="guide-category-tag">{data.category}</span>}
              <span>Updated {data.updatedLabel}</span>
            </div>

            {sections.length > 2 && (
              <nav className="guide-jumpto" aria-label="Jump to a section">
                <strong>Jump to</strong>
                <ul>
                  {sections.map((section) => (
                    <li key={section.id}><a href={`#${section.id}`}>{section.heading}</a></li>
                  ))}
                </ul>
              </nav>
            )}

            {showSbrRegSearch && (
              <GuideRegSearch
                heading="Check a vehicle while you read"
                copy={`Enter a UK registration to see what ${brand.name} holds for it.`}
              />
            )}

            {sections.map((section) => (
              <div key={section.id}>
                <h2 id={section.id}>{section.heading}</h2>
                {section.blocks.map((block, i) => <GuideBlock block={block} key={i} />)}
              </div>
            ))}

            {data.faq && (
              <div className="guide-faq">
                <h2>Common questions</h2>
                {data.faq.map((item, i) => (
                  <div key={i}>
                    <h3>{item.q}</h3>
                    <p>{item.a}</p>
                  </div>
                ))}
              </div>
            )}

            {related.length > 0 && (
              <div className="guide-related">
                <h2>Related guides</h2>
                <ul>
                  {pillar && <li><a href={pillar.href}>{pillar.label}</a></li>}
                  {related.map((guide) => (
                    <li key={guide.slug}><a href={guide.slug}>{guide.title}</a></li>
                  ))}
                </ul>
              </div>
            )}

            {showSbrRegSearch && (
              <GuideRegSearch
                heading="Check the car you are looking at"
                copy={`Enter a UK registration to open the free vehicle page on ${brand.name}.`}
              />
            )}

            {data.note && <div className="plain-note">{data.note}</div>}
          </div>
        </section>
      </InfoLayout>
    </>
  );
}
